import type { CSSProperties, ReactNode } from 'react';
import { Outlet } from 'react-router';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarProvider,
} from '@/shared/ui/components/sidebar';
import MenuTrigger from '@/components/shadcn-studio/blocks/menu-trigger';

type Props = {
  /** Branding row at the top of the sidebar (logo / app name). */
  sidebarHeader: ReactNode;
  /** Nav groups; render `SidebarGroup`s here. */
  sidebarContent: ReactNode;
  /** Accessible name for the sidebar's `navigation` landmark. */
  navLabel: string;
  /** Bottom-of-sidebar slot (account menu, cross-links). */
  sidebarFooter: ReactNode;
  /** Main header band title. */
  title: ReactNode;
  /** Optional muted line under the title. */
  subtitle?: ReactNode;
  /** Optional trailing controls in the header band. */
  actions?: ReactNode;
};

/**
 * Shared application chrome adapted from the shadcn-studio `dashboard-shell-05` block: a
 * floating, icon-collapsible card sidebar over a muted page, with a full-width primary
 * ("indigo") header band carrying the collapse toggle and title. Both the franchise
 * `AdminShell` and the `VenueShell` feed it slots so the look stays in one place while
 * each keeps its own permission-gated nav, i18n, and account controls.
 */
export function AppShellFrame({
  sidebarHeader,
  sidebarContent,
  sidebarFooter,
  navLabel,
  title,
  subtitle,
  actions,
}: Props) {
  return (
    <div className="bg-muted before:bg-primary relative flex min-h-dvh w-full before:fixed before:inset-x-0 before:top-0 before:h-32">
      <SidebarProvider
        style={
          {
            '--sidebar': 'var(--card)',
            '--sidebar-width': '17.5rem',
            '--sidebar-width-icon': '3.375rem',
          } as CSSProperties
        }
      >
        <Sidebar
          variant="floating"
          collapsible="icon"
          className="p-6 pr-0 *:data-[slot=sidebar-inner]:group-data-[variant=floating]:rounded-xl"
        >
          <SidebarHeader>{sidebarHeader}</SidebarHeader>
          <SidebarContent role="navigation" aria-label={navLabel}>
            {sidebarContent}
          </SidebarContent>
          <SidebarFooter>{sidebarFooter}</SidebarFooter>
        </Sidebar>
        <div className="z-1 flex min-w-0 flex-1 flex-col py-6">
          <header className="text-primary-foreground">
            <div className="flex w-full items-center justify-between gap-6 px-4 sm:px-6">
              <div className="flex min-w-0 items-center gap-4">
                <MenuTrigger
                  variant="outline"
                  className="bg-primary-foreground! border-primary-foreground! text-primary! shadow-none"
                />
                <div className="flex min-w-0 flex-col items-start">
                  <p className="truncate text-lg font-semibold">{title}</p>
                  {subtitle ? (
                    <p className="text-primary-foreground/70 truncate text-sm">{subtitle}</p>
                  ) : null}
                </div>
              </div>
              {actions ? <div className="flex items-center gap-1.5">{actions}</div> : null}
            </div>
          </header>
          <main className="min-w-0 flex-1 px-4 pt-6 sm:px-6">
            <div className="bg-card min-h-full overflow-hidden rounded-xl border shadow-sm">
              <Outlet />
            </div>
          </main>
        </div>
      </SidebarProvider>
    </div>
  );
}
